import React, { useState, useEffect } from "react";
import {
  useParams,
  useNavigate,
  useSearchParams,
  useOutletContext,
  useLocation,
} from "react-router-dom";
import ViewAddFeedback from "../home_screns/View_list_feedback";
import SharedFeedbackView from "../home_screns/Shared_Feedback_view_page_by_developer";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { DeveloperTokenManager } from "../../../utils/developerTokenManager";
import { API_URLS, BASE_URL } from "../../../api/urls";
import AvatarIcon from "../../../assets/images/profile-avatar.svg";
import { FeedbackItem } from "../../../types/feedback.types";
import { shareFeedbackWithDeveloper } from "../../../api/api-utils";
import { extractCardIdFromUrl, buildCardUrl } from "../../../utils/urlIdEncoder";

interface DashboardOutletContext {
  onHideSidebar: (hide: boolean) => void;
}

const Shimmer = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <div
    className={className}
    style={{
      background: "linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%)",
      backgroundSize: "600px 100%",
      animation: "shimmer 1.6s ease-in-out infinite",
      borderRadius: 6,
      ...style,
    }}
  />
);

const FeedbackPageSkeleton: React.FC = () => (
  <div className="h-screen w-full bg-white flex flex-col overflow-hidden">
    <style>{`
      @keyframes shimmer {
        0%   { background-position: -600px 0; }
        100% { background-position:  600px 0; }
      }
    `}</style>

    {/* Header */}
    <header className="px-6 pt-5 pb-2 flex-shrink-0">
      {/* Back button row */}
      <div className="flex items-center gap-2 mb-3">
        <Shimmer style={{ width: 12, height: 12, borderRadius: 3 }} />
        <Shimmer style={{ width: 32, height: 12, borderRadius: 4 }} />
      </div>

      {/* Title row */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Shimmer style={{ width: 180, height: 22, borderRadius: 6 }} />
          {/* Developer avatar group */}
          <div className="flex">
            <Shimmer style={{ width: 22, height: 22, borderRadius: "50%" }} />
            <Shimmer style={{ width: 22, height: 22, borderRadius: "50%", marginLeft: -6, opacity: 0.7 }} />
            <Shimmer style={{ width: 22, height: 22, borderRadius: "50%", marginLeft: -6, opacity: 0.45 }} />
          </div>
        </div>
        <div className="flex gap-2">
          <Shimmer style={{ width: 140, height: 36, borderRadius: 8 }} />
          <Shimmer style={{ width: 120, height: 36, borderRadius: 8 }} />
        </div>
      </div>
    </header>

    {/* Content */}
    <div className="flex flex-1 overflow-hidden px-4 pb-4">

      {/* Left sidebar */}
      <div className="flex flex-col" style={{ width: "27%", minWidth: 220 }}>
        {/* List header */}
        <div className="flex items-center justify-between pr-2 pb-2">
          <Shimmer style={{ width: 130, height: 16, borderRadius: 4 }} />
          <div className="flex gap-2">
            <Shimmer style={{ width: 28, height: 28, borderRadius: 7 }} />
            <Shimmer style={{ width: 28, height: 28, borderRadius: 7 }} />
          </div>
        </div>

        {/* Feedback list */}
        <div
          className="flex-1 flex flex-col gap-1 p-2 rounded-xl overflow-hidden"
          style={{ background: "#f7f7f7" }}
        >
          {[1, 0.9, 0.75, 0.6, 0.45].map((opacity, i) => (
            <div
              key={i}
              className="relative rounded-lg p-3 bg-white"
              style={{
                height: 112,
                opacity,
                borderLeft: i === 0 ? "4px solid #1E1F26" : "4px solid transparent",
              }}
            >
              <div className="flex gap-3">
                <Shimmer style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0 }} />
                <div className="flex-1">
                  <Shimmer style={{ width: "90%", height: 12, borderRadius: 4, marginBottom: 6 }} />
                  <Shimmer style={{ width: "65%", height: 12, borderRadius: 4 }} />
                </div>
              </div>
              {/* Number badge */}
              <div className="absolute bottom-3 left-3">
                <Shimmer style={{ width: 20, height: 11, borderRadius: 3 }} />
              </div>
              {/* Type icon */}
              <div className="absolute bottom-3 right-3">
                <Shimmer style={{ width: 16, height: 16, borderRadius: 4 }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right preview panel */}
      <div
        className="flex-1 flex flex-col overflow-hidden"
        style={{
          marginLeft: 10,
          marginTop: 10,
          border: "1px solid #C5D0F5",
          borderRadius: 10,
        }}
      >
        {/* Feedback text */}
        <div className="px-6 pt-5">
          <Shimmer style={{ width: "85%", height: 15, borderRadius: 5, marginBottom: 6 }} />
          <Shimmer style={{ width: "55%", height: 15, borderRadius: 5 }} />
        </div>

        {/* Screenshot / video area */}
        <div className="flex-1 px-6 py-4 flex items-center justify-center">
          <Shimmer style={{ width: "100%", maxHeight: 340, height: "100%", borderRadius: 12 }} />
        </div>

        {/* Bottom action bar: controls + divider + buttons */}
        <div>
          <div style={{ height: 1, background: "#E3E9EF" }} />
          <div
            className="flex items-center gap-3 px-4 py-2"
            style={{ background: "rgba(255,255,255,0.55)" }}
          >
            {/* Left: audio/video controls shimmer */}
            <div className="flex-1 flex items-center gap-2" style={{ height: 40 }}>
              <Shimmer style={{ width: 20, height: 20, borderRadius: "50%", flexShrink: 0 }} />
              <Shimmer style={{ flex: 1, height: 8, borderRadius: 4 }} />
            </div>

            {/* Vertical divider */}
            <div style={{ width: 2, alignSelf: "stretch", background: "#D5D9EA" }} />

            {/* Right: Unclear + Acknowledge buttons */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Shimmer style={{ width: 128, height: 36, borderRadius: 6 }} />
              <Shimmer style={{ width: 128, height: 36, borderRadius: 6 }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const getValidUrl = (path: string | null | undefined): string | undefined => {
  if (!path) return undefined;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${BASE_URL}${path.startsWith("/") ? "" : "/"}${path}`;
};

const FeedbackPage: React.FC = () => {
  const { cardId } = useParams<{ cardId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { onHideSidebar } = useOutletContext<DashboardOutletContext>();

  const navState = location.state as { activeTab?: string } | null;
  const isDraft = navState?.activeTab === "draft" || searchParams.get("tab") === "draft";
  const isDeveloper = searchParams.get("view") === "developer";

  const [loading, setLoading] = useState(true);
  const [cardName, setCardName] = useState("");
  const [feedbackItems, setFeedbackItems] = useState<FeedbackItem[]>([]);
  const [workspaceId, setWorkspaceId] = useState<number | undefined>();
  const [cardDbId, setCardDbId] = useState(0);

  useEffect(() => {
    onHideSidebar(false);
    loadCard();
  }, [cardId, isDraft, isDeveloper]);

  const buildFeedbackItems = (
    feedbacks: Array<{
      id: number;
      screenshot_url: string | null;
      recording_url: string | null;
      voice_recording_url: string | null;
      message: string | null;
      created_at: string;
      profile_image?: string | null;
      creator_name?: string | null;
    }>
  ): FeedbackItem[] => {
    return feedbacks
      .filter((f) => f.message || f.screenshot_url || f.recording_url || f.voice_recording_url)
      .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
      .map((f, index) => {
        const screenshotUrl = getValidUrl(f.screenshot_url);
        const recordingUrl = getValidUrl(f.recording_url);
        const audioUrl = getValidUrl(f.voice_recording_url);
        const itemType: "screenshot" | "recording" = recordingUrl ? "recording" : "screenshot";

        const item: FeedbackItem = {
          id: f.id.toString(),
          type: itemType,
          timestamp: new Date(f.created_at).getTime(),
          order: index + 1,
          feedback: f.message || "",
          hasAudio: !!audioUrl,
          audioUrl,
          audioFilename: audioUrl ? `audio_${f.id}.wav` : undefined,
          user: { name: "User", avatar: AvatarIcon },
          isSelected: false,
          apiFeedbackId: f.id,
          uniqueFeedbackId: f.id.toString(),
          profileImage: f.profile_image || undefined,
          creatorName: f.creator_name || undefined,
          technicalContext: (f as any).technical_context || undefined,
        };

        if (itemType === "screenshot") {
          item.annotatedImage = screenshotUrl;
          item.originalImage = screenshotUrl;
        } else {
          item.dataUrl = recordingUrl;
          item.duration = 30;
          item.recordingBlob = new Blob([], { type: "video/mp4" });
          // Load blob in background
          if (recordingUrl) {
            fetch(recordingUrl)
              .then((res) => (res.ok ? res.blob() : new Blob([], { type: "video/mp4" })))
              .catch(() => new Blob([], { type: "video/mp4" }))
              .then((blob) => {
                setFeedbackItems((prev) =>
                  prev.map((fi) => (fi.id === item.id ? { ...fi, recordingBlob: blob } : fi))
                );
              });
          }
        }

        return item;
      });
  };

  const loadCard = async () => {
    const numericCardId = extractCardIdFromUrl(cardId || '');
    if (!numericCardId) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const token = EnhancedTokenManager.getAccessToken();
    if (!token) {
      navigate("/dashboard", { replace: true });
      return;
    }

    const wId = EnhancedTokenManager.getWorkspaceIdForCurrentUser();
    if (wId) setWorkspaceId(wId);

    try {
      if (isDraft) {
        // Fetch from draft endpoint
        if (!wId) {
          navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
          return;
        }
        const response = await fetch(API_URLS.GET_DRAFT(wId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch drafts");
        const data = await response.json();
        const entries = Object.entries(data) as [string, any][];
        const entry = entries.find(([, cd]) => cd.card_id === numericCardId);
        if (!entry) {
          navigate("/dashboard", { state: { activeTab: "draft" }, replace: true });
          return;
        }
        const [, cardData] = entry;
        setCardName(cardData.card_name);
        setCardDbId(cardData.card_id);
        setFeedbackItems(buildFeedbackItems(cardData.feedbacks));
        setLoading(false);
      } else if (isDeveloper) {
        // Fetch from developer endpoint
        const userProfile = EnhancedTokenManager.getUserProfile();
        const userEmail = userProfile?.email;
        if (!userEmail) {
          navigate("/dashboard", { state: { activeTab: "shared" }, replace: true });
          return;
        }
        const devToken = await DeveloperTokenManager.getDeveloperTokenForEmail(userEmail);
        if (!devToken) {
          navigate("/dashboard", { state: { activeTab: "shared" }, replace: true });
          return;
        }
        const response = await fetch(API_URLS.VIEW_FEEDBACK_BY_DEVELOPER, {
          headers: { Authorization: `Bearer ${devToken}`, "X-Developer-Email": userEmail },
        });
        if (!response.ok) throw new Error("Failed to fetch developer cards");
        const data = await response.json();
        const card = data.find((c: any) => c.card_id === numericCardId);
        if (!card) {
          navigate("/dashboard", { state: { activeTab: "shared" }, replace: true });
          return;
        }
        setCardName(card.card_name);
        setCardDbId(card.card_id);
        setFeedbackItems(buildFeedbackItems(card.feedbacks));
        setLoading(false);
      } else {
        // Fetch from created/shared endpoint
        if (!wId) {
          navigate("/dashboard", { replace: true });
          return;
        }
        const response = await fetch(API_URLS.GET_CREATED_FEEDBACK(wId), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Failed to fetch created cards");
        const data = await response.json();
        const entries = Object.entries(data) as [string, any][];
        const entry = entries.find(([, cd]) => cd.card_id === numericCardId);
        if (!entry) {
          navigate("/dashboard", { replace: true });
          return;
        }
        const [, cardData] = entry;
        setCardName(cardData.card_name);
        setCardDbId(cardData.card_id);
        const rawFeedbacks = (cardData.feedbacks || []).map((item: any) => ({
          ...item.feedback,
          profile_image: item.feedback?.profile_image || null,
          creator_name: item.feedback?.creator_name || null,
        }));
        setFeedbackItems(buildFeedbackItems(rawFeedbacks));
        setLoading(false);
      }
    } catch (error) {
      console.error("Error loading feedback card:", error);
      navigate("/dashboard", { replace: true });
    }
  };

  const handleUpdateFeedback = (itemId: string, newText: string) => {
    setFeedbackItems((prev) =>
      prev.map((item) => (item.id === itemId ? { ...item, feedback: newText } : item))
    );
  };

  const handleUpdateAudio = (feedbackId: string, hasAudio: boolean, audioUrl?: string) => {
    setFeedbackItems((prev) =>
      prev.map((item) =>
        item.id === feedbackId
          ? { ...item, hasAudio, audioUrl, audioFilename: audioUrl ? `audio_${feedbackId}.wav` : undefined }
          : item
      )
    );
  };

  const handleDeleteFeedback = (itemId: string) => {
    setFeedbackItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  const handleShareWithDeveloper = async (shareData?: {
    success: boolean;
    cardId: number;
    feedbackName: string;
    emails: string[];
    feedbackCount: number;
  }) => {
    if (shareData?.success) {
      window.dispatchEvent(new CustomEvent("refresh-draft-tab"));
      navigate("/dashboard", {
        state: { activeTab: "created", refreshTab: "all" },
        replace: true,
      });
    }
  };

  if (loading) return <FeedbackPageSkeleton />;

  if (isDeveloper) {
    return (
      <SharedFeedbackView
        feedbackName={cardName}
        totalFeedbacks={feedbackItems.length}
        feedbackItems={feedbackItems}
        onBack={() => navigate("/dashboard", { state: { activeTab: "shared" }, replace: true })}
        onShareWithDeveloper={handleShareWithDeveloper}
        onUpdateAudio={handleUpdateAudio}
        onDeleteFeedback={handleDeleteFeedback}
        cardId={cardDbId}
        workspaceId={workspaceId}
        isSubmittedMode={true}
      />
    );
  }

  if (isDraft) {
    return (
      <ViewAddFeedback
        feedbackName={cardName}
        totalFeedbacks={feedbackItems.length}
        feedbackItems={feedbackItems}
        onBack={() => navigate("/dashboard", { state: { activeTab: "draft" }, replace: true })}
        onShareWithDeveloper={handleShareWithDeveloper}
        onUpdateFeedback={handleUpdateFeedback}
        onUpdateAudio={handleUpdateAudio}
        onDeleteFeedback={handleDeleteFeedback}
        cardId={cardDbId}
        workspaceId={workspaceId}
        isSubmittedMode={false}
        showDeveloperStatus={false}
        showBackButton={true}
        onGoBackToDraft={() => navigate("/dashboard", { state: { activeTab: "draft" }, replace: true })}
        onContinueEditing={() => navigate(`/dashboard/workspace/${buildCardUrl(cardDbId, cardName)}`, { replace: true })}
      />
    );
  }

  return (
    <ViewAddFeedback
      feedbackName={cardName}
      totalFeedbacks={feedbackItems.length}
      feedbackItems={feedbackItems}
      onBack={() => navigate("/dashboard", { replace: true })}
      onShareWithDeveloper={handleShareWithDeveloper}
      onUpdateFeedback={handleUpdateFeedback}
      onUpdateAudio={handleUpdateAudio}
      onDeleteFeedback={handleDeleteFeedback}
      cardId={cardDbId}
      workspaceId={workspaceId}
      isSubmittedMode={true}
      showDeveloperStatus={true}
      showBackButton={false}
    />
  );
};

export default FeedbackPage;
